/* import __COLOCATED_TEMPLATE__ from './trigger-info-node.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import type IntlService from 'embercom/services/intl';
import type WorkflowTitleEditorService from 'embercom/services/workflow-title-editor';
import { customBotObjectTypes } from 'embercom/models/data/matching-system/matching-constants';
import { preferredDevices } from 'embercom/models/data/outbound/constants';
import { SideSheetKeys } from 'embercom/objects/workflows/graph-editor/editor-state';
import {
  type PredicateOption,
  type TriggerPredicates,
} from 'embercom/objects/operator/configuration/custom-bot';
import {
  CHANNEL_METADATA_MAPPING,
  CHANNEL_GROUP_MAPPING,
  WEBHOOK_RECEIVED_TARGET,
  AI_AGENT_WORKFLOW_TARGET,
  SHOULD_TRIGGER_EXAMPLES,
  SHOULD_NOT_TRIGGER_EXAMPLES,
  GOAL_NUMBER_OF_EXAMPLES,
} from 'embercom/lib/operator/custom-bots/constants';
import { type Block, type AttributePredicate } from '@intercom/interblocks.ts';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import type Store from '@ember-data/store';

import { states } from 'embercom/models/data/matching-system/matching-constants';
import type EditorConfig from 'embercom/objects/visual-builder/configuration/editor';
import type ContentDependency from 'embercom/models/matching-system/content-dependency';
import { isBlank, isEmpty } from '@ember/utils';
import { BaseConfig, BlocksDocument } from '@intercom/composer-ember';
import { type ToolConfig } from '@intercom/composer-ember/lib/config/composer-config';
import { type Paragraph } from 'embercom/models/common/blocks/paragraph';
import { htmlUnescape } from 'embercom/lib/html-unescape';
import type CustomBotData from 'embercom/models/operator/custom-bots/custom-bot-data';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type StandardizedPlatform from 'embercom/objects/standalone/platforms/standardized-platform';

interface Args {
  editorConfiguration: EditorConfig;
  editorState: EditorState;
  infoPanelTriggerData: $TSFixMe;
  workflow: Workflow;
  readOnly: boolean;
  isTemplatePreview?: boolean;
  sideSheetIsDismissed?: boolean;
  selectedTemplate?: any;
}

class ComposerConfig extends BaseConfig {
  tools: ToolConfig[] = [];
  isPlaintext = true;
}
const MAX_NB_CHARS = 30;

export default class TriggerInfoNode extends Component<Args> {
  @service declare appService: AppService;
  @service declare contentEditorService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare workflowTitleEditor: WorkflowTitleEditorService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;
  @service declare finStandaloneService: FinStandaloneService;

  @tracked linkedWorkflows: ContentDependency[] = [];
  @tracked blocksDoc: BlocksDocument = new BlocksDocument([]);
  @tracked isWorkflowTriggerCardInFocus = true;
  @tracked shouldTriggerExamplesModal = false;
  @tracked shouldNotTriggerExamplesModal = false;

  liveState = states.live;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    if (!this.args.isTemplatePreview && !this.args.workflow.isFinPreview) {
      taskFor(this.fetchLinkedWorkflows).perform();
    }

    if (this.customBot?.isAiAgentWorkflowTrigger && this.description) {
      // Convert plain text to blocks format for proper rendering
      let text = this.description;
      // Handle both <br> and \n line breaks
      let lines = text.split('<br>').flatMap((line: string) => line.split('\n'));
      let blocks: Block[] = [];

      lines.forEach((line: string) => {
        blocks.push({
          type: 'paragraph',
          text: line,
          class: 'no-margin',
        });
      });

      this.blocksDoc = new BlocksDocument(blocks);
    }

    // Initialize the title editor service with current model value
    if (this.canUseDraftWorkflow) {
      this.workflowTitleEditor.initialize(this.args.workflow.additionalData.title);
    } else {
      this.workflowTitleEditor.initialize(this.ruleset?.clientData?.title);
    }

    if (
      this.appService.app.canUseFeature('workflows-fin-audiences') &&
      !this.aiContentSegmentsService.isLoading
    ) {
      this.aiContentSegmentsService.loadSegments();
    }
  }

  @restartableTask
  *fetchLinkedWorkflows() {
    this.linkedWorkflows = yield this.ruleset.loadDependentObjects();
  }

  get composerConfig() {
    let config = new ComposerConfig();
    config.placeholder = this.intl.t(
      'operator.workflows.visual-builder.workflow-trigger-node.description-empty-state',
    );
    config.allowedInline = [];
    config.allowedBlocks = ['paragraph'];
    config.autoFocus = false;
    return config;
  }

  @action
  onComposerReady(readOnly: boolean, composerApi: any) {
    if (readOnly) {
      // Dynamically disable interactivity when in read-only mode
      composerApi?.composer?.disableInteractivity();
    } else {
      // Ensure interactivity is enabled when not in read-only mode
      composerApi?.composer?.enableInteractivity();
    }
  }

  get sideSheetKeys() {
    return SideSheetKeys;
  }

  get isAvailableFromInbox() {
    return this.allowManualTrigger && this.isWorkflowLive;
  }

  get isWorkflowLive() {
    return this.ruleset.state === states.live;
  }

  get allowManualTrigger() {
    return this.botData.allowManualTrigger;
  }

  get customBot() {
    return this.contentEditorService.activeObject;
  }

  get ruleset() {
    return this.contentEditorService.ruleset;
  }

  // Helper getters to read audience data from draft or ruleset
  // These fall back to ruleset if draft data is null (e.g., after publish when additionalData is cleared)
  get currentPredicateGroup() {
    if (this.canUseDraftWorkflow) {
      let draftData = this.customBot?.activeWorkflow?.additionalData?.predicateGroup;
      if (draftData) {
        return draftData;
      }
    }
    return this.ruleset?.predicateGroup;
  }

  get currentRolePredicateGroup() {
    if (this.canUseDraftWorkflow) {
      // Use draft data if available, otherwise fall back to ruleset
      // This handles the post-publish state where additionalData is cleared but canUseDraftWorkflow is still true
      let draftData = this.customBot?.activeWorkflow?.additionalData?.rolePredicateGroup;
      if (draftData) {
        return draftData;
      }
    }
    return this.ruleset?.rolePredicateGroup;
  }

  get currentSegmentIds() {
    if (this.canUseDraftWorkflow) {
      let draftData = this.customBot?.activeWorkflow?.additionalData?.segmentIds;
      if (draftData && draftData.length > 0) {
        return draftData;
      }
    }
    return this.ruleset?.segmentIds || [];
  }

  get rulesetHasPredicates() {
    return this.currentPredicateGroup?.hasPredicates;
  }

  get rulesetPredicatesCount() {
    return (
      this.currentPredicateGroup?.predicates?.firstObject?.predicates?.length ||
      this.currentPredicateGroup?.predicates?.length ||
      0
    );
  }

  get rulesetHasUserPredicates() {
    return this.currentRolePredicateGroup?.userPredicate;
  }

  get rulesetHasFinAudience() {
    return (
      this.appService.app.canUseFeature('workflows-fin-audiences') &&
      this.currentSegmentIds?.length > 0
    );
  }

  get showAdditionalInfo() {
    if (this.isSeriesModeOrTemplatePreview) {
      return false;
    }

    if (this.appService.app.canUseStandalone && !this.sideSheetEnabled) {
      return false;
    }

    return (
      this.isAvailableFromInbox ||
      this.hasLinkedWorkflows ||
      (this.shouldRenderChannels && !this.isTriggerBot) ||
      ((this.rulesetHasUserPredicates || this.rulesetHasFinAudience) && !this.isTriggerBot)
    );
  }

  get infoTooltip() {
    // Should show tooltip only if more than 1 predicate was selected
    return this.args.infoPanelTriggerData.triggerPredicatesDescriptionTranslationKey;
  }

  get hasMultiplePredicatesSelected() {
    return this.selectedTriggerPredicates?.length > 1;
  }

  get segmentDetails(): any {
    let selectedIds = this.currentSegmentIds;
    let segments = this.aiContentSegmentsService.segments;
    let firstSegment = segments?.find((segment: any) => selectedIds[0] === Number(segment.id));

    if (!firstSegment) {
      return;
    }

    let firstSegmentName = firstSegment.nameWithFallback;
    if (firstSegmentName && firstSegmentName.length > MAX_NB_CHARS) {
      firstSegmentName = `${firstSegmentName.substring(0, MAX_NB_CHARS)}...`;
    }

    if (selectedIds.length === 1) {
      return firstSegmentName;
    }

    return this.intl.t('common.audience-selector-dropdown.additional-segments', {
      segmentName: firstSegmentName,
      count: selectedIds.length - 1,
    });
  }

  get userTypes() {
    if (this.appService.app.canUseFeature('workflows-fin-audiences')) {
      return this.selectedAudience;
    }
    return this.currentRolePredicateGroup?.userPredicate?.userTypes || [];
  }

  get selectedAudience() {
    let hasPredicates = this.currentPredicateGroup?.predicates?.length > 0;
    let hasRolePredicates = this.currentRolePredicateGroup?.predicates?.length > 0;
    if (this.rulesetHasFinAudience && this.segmentDetails) {
      return [this.segmentDetails];
    } else if (hasRolePredicates || hasPredicates) {
      return this.currentRolePredicateGroup?.userPredicate?.userTypes;
    } else {
      return [this.intl.t('common.audience-selector-dropdown.everyone')];
    }
  }

  get allUserRoles() {
    return ['user', 'lead', 'visitor'];
  }

  get triggerPredicates(): TriggerPredicates | null {
    return this.args.infoPanelTriggerData.triggerPredicates;
  }

  get hasLinkedWorkflows() {
    return this.linkedWorkflows.length > 0;
  }

  get linkedWorkflowsSummary() {
    return {
      items: this.linkedWorkflows.slice(0, 2),
      remainingCount: Math.max(this.linkedWorkflows.length - 2, 0),
    };
  }

  get targetChannels() {
    let channels;

    if (this.canUseDraftWorkflow) {
      // For inbound workflows, use preferredDevices (Web/iOS/Android)
      // For other workflows (AI Agent), use targetChannels (messenger/email/SMS/etc.)
      if (this.customBot.isInbound) {
        channels = this.customBot.activeWorkflow.additionalData.preferredDevices;
      } else {
        channels = this.customBot.activeWorkflow.additionalData.targetChannels;
      }
    } else if (this.customBot.isInbound) {
      // For non-draft mode, inbound workflows use preferredDevices
      channels = this.customBot.preferredDevices;
    } else {
      channels = this.customBot.targetChannels;
    }

    // Convert numeric preferredDevices values to string channel names
    // preferredDevices: [0, 1, 2] -> ['web', 'ios', 'android']
    if (this.customBot.isInbound && channels) {
      // Invert the preferredDevices constant: { web: 0, ios: 1, android: 2 } -> { 0: 'web', 1: 'ios', 2: 'android' }
      let deviceMap = Object.fromEntries(
        Object.entries(preferredDevices).map(([name, id]) => [id, name]),
      );
      return (channels as number[]).map((device) => deviceMap[device]).filter(Boolean);
    }

    return channels;
  }

  get channels() {
    let selectedChannels = this.targetChannels as
      | (keyof typeof CHANNEL_METADATA_MAPPING)[]
      | undefined;

    if (!selectedChannels || !selectedChannels.length) {
      return undefined;
    }

    let channelMapping = CHANNEL_GROUP_MAPPING;

    return Object.values(channelMapping)
      .flat()
      .filter((channel) => selectedChannels?.includes(channel))
      .map((channel) => CHANNEL_METADATA_MAPPING[channel]);
  }

  get localizedList() {
    if (!this.targetChannels) {
      return [];
    }

    let selectedChannels = this.targetChannels as
      | (keyof typeof CHANNEL_METADATA_MAPPING)[]
      | undefined;

    return (selectedChannels || []).map((channel) => {
      let metadata = CHANNEL_METADATA_MAPPING[channel];
      return this.intl.t('operator.workflows.channels.type', { channel: metadata.value });
    });
  }

  get selectedChannelsLabel() {
    if (this.localizedList.length === 0) {
      return '';
    }

    if (this.localizedList.length === 1) {
      return this.intl.t(
        'operator.workflows.visual-builder.workflow-trigger-node.selected-channel.single-channel',
        {
          channel: this.localizedList[0],
        },
      );
    } else {
      return this.intl.t(
        'operator.workflows.visual-builder.workflow-trigger-node.selected-channel.double-channel',
        {
          channelOne: this.localizedList[0],
          channelTwo: this.localizedList[1],
        },
      );
    }
  }

  get remainingSelectedChannelsCount() {
    if (this.localizedList.length - 2 <= 0) {
      return 0;
    }

    return this.localizedList.length - 2;
  }

  get showEnvironmentSection() {
    if (!this.appService.app.canUseStandaloneMultiEnv) {
      return false;
    }

    // Don't show environment section for tasks and procedures
    if (this.customBot?.isAiAgentWorkflowTrigger) {
      return false;
    }

    let platform = this.finStandaloneService.platform as StandardizedPlatform<any>;
    let configurations = platform.configurations ?? [];
    let activeConfiguration = platform.activeConfiguration;

    return configurations.length > 1 || activeConfiguration?.isConnected;
  }

  get selectedTriggerPredicates(): PredicateOption[] {
    if (this.args.isTemplatePreview) {
      return [];
    }

    if (!this.triggerPredicates || !this.isLogicalTriggerPredicate(this.triggerPredicates.type)) {
      return [];
    }

    let selectedPredicates =
      this.customBot.triggerPredicateGroup?.predicates.firstObject?.predicates.map(
        (o: AttributePredicate) => o.value,
      );

    if (!selectedPredicates) {
      return [];
    }

    return this.triggerPredicates.options.filter((option) =>
      selectedPredicates.includes(option.value),
    );
  }

  isLogicalTriggerPredicate(type: any) {
    return ['or', 'and'].includes(type);
  }

  get analyticsData() {
    return {
      ...this.args.workflow.analyticsData,
      object: 'visual_builder_trigger_info_node',
      node: 'trigger_info_node',
      overwriteObject: true,
    };
  }

  get shouldTriggerExamplesCount() {
    try {
      return JSON.parse(this.botData.shouldTriggerExamples || '[]').length;
    } catch (error) {
      return 0;
    }
  }

  get shouldNotTriggerExamplesCount() {
    try {
      return JSON.parse(this.botData.shouldNotTriggerExamples || '[]').length;
    } catch (error) {
      return 0;
    }
  }

  get botData(): CustomBotData {
    if (this.canUseDraftWorkflow) {
      return this.args.workflow.additionalData.customBotData || this.customBot.botData;
    }
    return this.customBot.botData;
  }

  get goalNumberOfExamples() {
    return GOAL_NUMBER_OF_EXAMPLES;
  }

  get hasGoalNumberOfShouldTriggerExamples() {
    return this.shouldTriggerExamplesCount >= GOAL_NUMBER_OF_EXAMPLES;
  }

  get hasGoalNumberOfShouldNotTriggerExamples() {
    return this.shouldNotTriggerExamplesCount >= GOAL_NUMBER_OF_EXAMPLES;
  }

  get shouldTriggerExamplesType() {
    return SHOULD_TRIGGER_EXAMPLES;
  }

  get shouldNotTriggerExamplesType() {
    return SHOULD_NOT_TRIGGER_EXAMPLES;
  }

  @action
  openShouldTriggerExamplesModal() {
    this.shouldTriggerExamplesModal = true;
  }

  @action
  openShouldNotTriggerExamplesModal() {
    this.shouldNotTriggerExamplesModal = true;
  }

  @action
  closeShouldTriggerExamplesModal() {
    this.shouldTriggerExamplesModal = false;
  }

  @action
  closeShouldNotTriggerExamplesModal() {
    this.shouldNotTriggerExamplesModal = false;
  }

  get isTriggerBot() {
    if (this.args.isTemplatePreview) {
      return false;
    }

    return this.customBot.isTriggerable && this.customBot.isNoTrigger;
  }

  get isSeriesMode() {
    return this.contentEditorService.isSeriesMode;
  }

  get isSeriesModeOrTemplatePreview() {
    return this.isSeriesMode || this.args.isTemplatePreview;
  }

  get sideSheetEnabled() {
    if (this.isSeriesModeOrTemplatePreview) {
      return false;
    }

    return this.args.editorConfiguration.supportsSideSheet;
  }

  get customBotDescriptionIsBlank() {
    return isEmpty(this.description);
  }

  get hasErrors() {
    if (this.isSeriesModeOrTemplatePreview) {
      return false;
    }

    if (isBlank(this.title)) {
      return true;
    }

    if (this.customBot.isAiAgentWorkflowTrigger && isBlank(this.description)) {
      return true;
    }

    // We only count errors that are not errors in the workflow
    return (
      this.ruleset.validations.errors.length -
        this.ruleset.rulesetLinks.reduce((previous: number, current: any) => {
          if (customBotObjectTypes.includes(current.objectType)) {
            return (
              previous +
              (current.object.validations.attrs.visualBuilderObject.errors.length as number)
            );
          } else {
            return previous;
          }
        }, 0) >
        0 && this.args.editorState.shouldShowValidations
    );
  }

  get aiWorkflowTriggerErrorMessages() {
    let missingFields = [];

    if (isBlank(this.title)) {
      missingFields.push(
        this.intl.t(
          'operator.workflows.visual-builder.trigger-info-node-content.ai-agent-workflow.title',
        ),
      );
    }

    if (isBlank(this.description)) {
      missingFields.push(
        this.intl.t(
          'operator.workflows.visual-builder.trigger-info-node-content.ai-agent-workflow.trigger-description-title',
        ),
      );
    }

    if (missingFields.length === 0) {
      return [];
    } else {
      let errorMsg = this.intl.t(
        'operator.workflows.visual-builder.workflow-trigger-node.missing-details-error',
        { missingFields: missingFields.join(', ') },
      );
      return [errorMsg];
    }
  }

  get description() {
    if (this.canUseDraftWorkflow) {
      return this.args.workflow.additionalData.description;
    }
    return this.customBot.description;
  }

  @action
  updateBlocksDoc() {
    // Convert plain text to blocks format for proper rendering
    let text = this.description;
    // Handle both <br> and \n line breaks
    let lines = text.split('<br>').flatMap((line: string) => line.split('\n'));
    let blocks: Block[] = [];

    lines.forEach((line: string) => {
      blocks.push({
        type: 'paragraph',
        text: line,
        class: 'no-margin',
      });
    });

    this.blocksDoc = new BlocksDocument(blocks);
  }

  get isWebhookReceived() {
    return this.args.infoPanelTriggerData.target === WEBHOOK_RECEIVED_TARGET;
  }

  get isAiAgentWorkflow() {
    return this.args.infoPanelTriggerData.target === AI_AGENT_WORKFLOW_TARGET;
  }

  get shouldRenderChannels(): boolean {
    let result = this.localizedList.length > 0 && !this.isTriggerBot && !this.isWebhookReceived;
    return result && this.args.editorConfiguration.supportsConversationChannelTargeting;
  }

  get shouldRenderAudienceSummary(): boolean {
    return (
      this.args.editorConfiguration.supportsUserTargeting &&
      (this.rulesetHasUserPredicates || this.rulesetHasFinAudience)
    );
  }

  get inboundTriggerDescription() {
    let inboundTriggerUUID =
      this.customBot?.triggerPredicateGroup?.predicates?.firstObject?.predicates?.firstObject
        ?.value;
    let trigger = this.store
      .peekAll('workflow-connector/inbound-trigger')
      .findBy('uuid', inboundTriggerUUID);

    let translationKey = null;
    if (trigger?.name === 'Jira Issue Updated') {
      translationKey =
        'operator.custom-bot.trigger.webhook-received.jira-issue-updated-description';
    } else if (trigger?.name === 'Jira Comment Created') {
      translationKey =
        'operator.custom-bot.trigger.webhook-received.jira-issue-status-updated-description';
    }

    return translationKey;
  }

  @action
  onChange(blocks: BlocksDocument) {
    let joinedBlocks = blocks.blocks.map((b: Paragraph) => htmlUnescape(b.text)).join('\n');
    if (this.canUseDraftWorkflow) {
      this.args.workflow.additionalData.description = joinedBlocks;
    } else {
      this.customBot.description = joinedBlocks;
    }
  }

  get title() {
    return this.workflowTitleEditor.currentTitle;
  }

  @action
  onChangeTitle(value: string) {
    this.workflowTitleEditor.updateTitle(value);
  }

  @action
  onTitleFocusOut() {
    let finalValue = this.workflowTitleEditor.currentTitle;
    let trimmedValue = finalValue.trim();

    if (this.canUseDraftWorkflow) {
      this.args.workflow.additionalData.title = trimmedValue;
    } else {
      this.ruleset.clientData.title = trimmedValue;
    }
  }

  get canUseDraftWorkflow() {
    if (this.args.isTemplatePreview) {
      return false;
    }
    return this.customBot.canUseDraftWorkflow();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::TriggerInfoNode': typeof TriggerInfoNode;
    'workflows/graph-editor/trigger-info-node': typeof TriggerInfoNode;
  }
}
